import React, { useState, useEffect } from 'react';
import { DialogComponent } from '@syncfusion/ej2-react-popups';
import { ButtonComponent, CheckBoxComponent } from '@syncfusion/ej2-react-buttons';
import { DateTimePickerComponent } from '@syncfusion/ej2-react-calendars';
import { DropDownListComponent } from '@syncfusion/ej2-react-dropdowns';
import { NumericTextBoxComponent } from '@syncfusion/ej2-react-inputs';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { L10n } from '@syncfusion/ej2-base';
import { type ShiftAssignmentDto } from '../../api/models/ShiftAssignmentDto';
import { type ShiftTemplateDto } from '../../api/models/ShiftTemplateDto';
import { ShiftTemplateControllerService } from '../../api/services/ShiftTemplateControllerService';
import { useErrorHandler } from '../../utils/errorHandler';
import { generateTempUUID } from '../../utils/uuid';

// Type for shift assignment with string id for CrudPage compatibility
type ShiftAssignmentWithStringId = Omit<ShiftAssignmentDto, 'id'> & { id: string };

// Helper function to get first day of current month at 00:00
const getFirstDayOfCurrentMonth = (): Date => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0);
};

// Maximum date: 31.12.2050 23:59
const MAX_DATE = new Date(2050, 11, 31, 23, 59, 59);

// Create validation schema factory that accepts isSingleResource parameter
const createShiftAssignmentSchema = (isSingleResource: boolean) => {
  const capacityValidation = isSingleResource
    ? z.number().min(0.1, 'Capacity must be at least 0.1').max(1.0, 'Capacity cannot exceed 1.0 for single resource machines')
    : z.number().min(0.1, 'Capacity must be at least 0.1');
  
  return z.object({
    assignmentStart: z.union([
      z.date(),
      z.string().min(1, 'Start date is required')
    ]).transform((val) => {
      if (val instanceof Date) {
        // Convert Date to ISO string format: YYYY-MM-DDTHH:mm:ss
        const year = val.getFullYear();
        const month = String(val.getMonth() + 1).padStart(2, '0');
        const day = String(val.getDate()).padStart(2, '0');
        const hours = String(val.getHours()).padStart(2, '0');
        const minutes = String(val.getMinutes()).padStart(2, '0');
        const seconds = String(val.getSeconds()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
      }
      // If it's already a string, ensure it's in ISO format
      if (typeof val === 'string') {
        // Replace space with T if present
        return val.replace(' ', 'T');
      }
      return val;
    }),
    assignmentEnd: z.union([
      z.date(),
      z.string().min(1, 'End date is required')
    ]).transform((val) => {
      if (val instanceof Date) {
        // Convert Date to ISO string format: YYYY-MM-DDTHH:mm:ss
        const year = val.getFullYear();
        const month = String(val.getMonth() + 1).padStart(2, '0');
        const day = String(val.getDate()).padStart(2, '0');
        const hours = String(val.getHours()).padStart(2, '0');
        const minutes = String(val.getMinutes()).padStart(2, '0');
        const seconds = String(val.getSeconds()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
      }
      // If it's already a string, ensure it's in ISO format
      if (typeof val === 'string') {
        // Replace space with T if present
        return val.replace(' ', 'T');
      }
      return val;
    }),
    shiftTemplateId: z.string().min(1, 'Shift template is required'),
    capacityValue: capacityValidation,
    overwritesLowerPriority: z.boolean().optional()
  }).refine((data) => {
    // Validate that assignmentEnd is after assignmentStart
    // After transform, both are strings in ISO format
    try {
      const start = new Date(data.assignmentStart);
      const end = new Date(data.assignmentEnd);
      
      if (isNaN(start.getTime()) || isNaN(end.getTime())) {
        return true; // Let individual field validation handle invalid dates
      }
      
      return end > start;
    } catch (error) {
      // If parsing fails, let individual field validation handle it
      return true;
    }
  }, {
    message: 'End date must be after start date',
    path: ['assignmentEnd']
  });
};

// Override the type to allow Date | string for date fields (before transform)
type ShiftAssignmentFormData = {
  assignmentStart: string | Date;
  assignmentEnd: string | Date;
  shiftTemplateId: string;
  capacityValue: number;
  overwritesLowerPriority?: boolean;
};

type ShiftAssignmentFormProps = {
  value: ShiftAssignmentWithStringId | null;
  onClose: () => void;
  onSave: (value: ShiftAssignmentDto) => Promise<void> | void;
  resourceId?: string;
  shiftTemplates: ShiftTemplateDto[];
  isSingleResource?: boolean;
};

const ShiftAssignmentFormDialog: React.FC<ShiftAssignmentFormProps> = ({ 
  value, 
  onClose, 
  onSave, 
  resourceId,
  shiftTemplates,
  isSingleResource = false
}) => {
  const { t, i18n } = useTranslation(['common']);
  const [visible, setVisible] = useState(false);
  const [localShiftTemplates, setLocalShiftTemplates] = useState<ShiftTemplateDto[]>([]);
  const { handleError } = useErrorHandler();
  
  // Set German locale for DateTimePicker - load immediately on mount and when language changes
  useEffect(() => {
    if (i18n.language === 'de') {
      // Load German translations for datepicker/calendar (Today button)
      L10n.load({
        'de-DE': {
          'datepicker': {
            'today': 'Heute',
            'placeholder': 'Datum auswählen'
          },
          'calendar': {
            'today': 'Heute'
          },
          'numerictextbox': {
            'incrementTitle': 'Erhöhen',
            'decrementTitle': 'Verringern'
          }
        }
      });
    }
  }, [i18n.language]);
  
  // Helper function to convert string to Date object
  const parseDateString = (dateString: string | undefined): Date | null => {
    if (!dateString) return null;
    // Try parsing ISO format: "YYYY-MM-DD HH:mm:ss" or "YYYY-MM-DDTHH:mm:ss"
    const date = new Date(dateString.replace(' ', 'T'));
    return isNaN(date.getTime()) ? null : date;
  };

  // Create schema based on isSingleResource
  const shiftAssignmentSchema = React.useMemo(
    () => createShiftAssignmentSchema(isSingleResource),
    [isSingleResource]
  );

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitting }
  } = useForm<ShiftAssignmentFormData>({
    resolver: zodResolver(shiftAssignmentSchema),
    mode: 'onSubmit',
    defaultValues: {
      assignmentStart: '',
      assignmentEnd: '',
      shiftTemplateId: undefined,
      capacityValue: isSingleResource ? 1.0 : 1.0,
      overwritesLowerPriority: false
    }
  });

  useEffect(() => {
    if (isSingleResource) {
      setValue('capacityValue', 1.0, { shouldValidate: true, shouldDirty: true });
      setValue('overwritesLowerPriority', true, { shouldValidate: true, shouldDirty: true });
    }
  }, [isSingleResource, setValue]);

  useEffect(() => {
    if (value) {
      // Convert string dates to Date objects for DateTimePicker
      const startDate = parseDateString(value.assignmentStart);
      const endDate = parseDateString(value.assignmentEnd);

      reset({
        assignmentStart: startDate || getFirstDayOfCurrentMonth(),
        assignmentEnd: endDate || MAX_DATE,
        shiftTemplateId: value.shiftTemplateId || '',
        capacityValue: value.capacityValue || 1.0,
        overwritesLowerPriority: value.overwritesLowerPriority ?? false
      });
    } else {
      reset({
        assignmentStart: getFirstDayOfCurrentMonth(),
        assignmentEnd: MAX_DATE,
        shiftTemplateId: '',
        capacityValue: 1.0,
        overwritesLowerPriority: false
      });
    }
    setVisible(true);
  }, [value, reset]);

  useEffect(() => {
    if (shiftTemplates && shiftTemplates.length > 0) {
      return;
    }
    const loadShiftTemplates = async () => {
      try {
        const templates = await ShiftTemplateControllerService.findAll();
        setLocalShiftTemplates(templates);
      } catch (error) {
        handleError(error, 'loading shift templates');
      }
    };
    loadShiftTemplates();
  }, [shiftTemplates, handleError]);

  const onSubmit = async (data: ShiftAssignmentFormData) => {
    try {
      // Convert Date objects to ISO strings if needed
      const assignmentStartStr = data.assignmentStart instanceof Date 
        ? data.assignmentStart.toISOString().replace('T', ' ').substring(0, 19)
        : data.assignmentStart;
      const assignmentEndStr = data.assignmentEnd instanceof Date 
        ? data.assignmentEnd.toISOString().replace('T', ' ').substring(0, 19)
        : data.assignmentEnd;
      
      const shiftAssignment: ShiftAssignmentDto = {
        ...value,
        uuid: value?.uuid || generateTempUUID(), // Generate temp UUID if not present (for new assignments)
        assignmentStart: assignmentStartStr,
        assignmentEnd: assignmentEndStr,
        resourceId: resourceId?.toString() ?? value?.resourceId,
        shiftTemplateId: data.shiftTemplateId,
        capacityValue: isSingleResource ? 1.0 : data.capacityValue,
        overwritesLowerPriority: isSingleResource ? true : (data.overwritesLowerPriority ?? false)
        // priority will be set by the parent (ResourceFormDialog) based on position in grid
      };
      
      await onSave(shiftAssignment);
      // Only close dialog if save was successful
      setVisible(false);
      // Delay onClose to allow React to clean up components and avoid DOM exceptions
      setTimeout(() => {
        onClose();
      }, 100);
    } catch (error) {
      // Handle error but keep dialog open
      handleError(error, 'saving shift assignment');
      // Don't close the dialog on error
    }
  };

  const handleClose = () => {
    setVisible(false);
    // Delay onClose to allow React to clean up components and avoid DOM exceptions
    setTimeout(() => {
    onClose();
    }, 100);
  };

    // Get all validation errors
    const hasErrors = Object.keys(errors).length > 0;
    
    return (
    <DialogComponent
      width="500px"
      isModal={true}
      visible={visible}
      close={handleClose}
      beforeClose={(e: any) => {
        // Prevent default close behavior and use our own handleClose
        e.cancel = true;
        handleClose();
      }}
      showCloseIcon={true}
      closeOnEscape={true}
      target="body"
      animationSettings={{ effect: 'None' }}
      cssClass="shift-assignment-form-dialog"
      zIndex={10000}
      header={value ? t('shiftAssignment.form.editTitle') : t('shiftAssignment.form.createTitle')}
      footerTemplate={() => (
        <div className="flex justify-end space-x-2">
          <ButtonComponent
            type="button"
            onClick={handleClose}
            cssClass="e-outline"
          >
            {t('actions.cancel')}
          </ButtonComponent>
          <ButtonComponent
            type="button"
            onClick={handleSubmit(onSubmit as any)}
            disabled={isSubmitting}
            cssClass="e-primary"
          >
            {t('actions.ok')}
          </ButtonComponent>
        </div>
      )}
    >
      <div className="p-4 space-y-4">
        {/* General validation errors display */}
        {hasErrors && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <div className="text-red-800 text-sm font-medium mb-1">
              {t('form.validationErrors', 'Please fix the following errors:')}
            </div>
            <ul className="text-red-700 text-sm list-disc list-inside">
              {errors.assignmentStart && (
                <li>{t('shiftAssignment.form.field.assignmentStart')}: {errors.assignmentStart.message || t('form.required')}</li>
              )}
              {errors.assignmentEnd && (
                <li>{t('shiftAssignment.form.field.assignmentEnd')}: {errors.assignmentEnd.message || t('form.required')}</li>
              )}
              {errors.shiftTemplateId && (
                <li>{t('shiftAssignment.form.field.shiftTemplate')}: {errors.shiftTemplateId.message || t('form.required')}</li>
              )}
              {errors.capacityValue && (
                <li>{t('shiftAssignment.form.field.capacityValue')}: {errors.capacityValue.message || t('form.required')}</li>
              )}
            </ul>
          </div>
        )}
        
        {/* Assignment Start */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('shiftAssignment.form.field.assignmentStart')} *
          </label>
          <Controller
            name="assignmentStart"
            control={control}
            render={({ field }) => {
              // Convert string to Date if needed
              const dateValue = field.value instanceof Date 
                ? field.value 
                : (typeof field.value === 'string' ? parseDateString(field.value) : null) || getFirstDayOfCurrentMonth();
              
              return (
                <DateTimePickerComponent
                  value={dateValue}
                  onChange={(e: any) => {
                    // DateTimePicker returns Date object or null
                    field.onChange(e.value);
                  }}
                  placeholder={t('shiftAssignment.form.field.assignmentStart')}
                  cssClass={errors.assignmentStart ? 'e-error' : ''}
                  format={i18n.language === 'de' ? 'dd.MM.yyyy HH:mm' : 'MM/dd/yyyy hh:mm a'}
                  locale="de-DE"
                />
              );
            }}
          />
          {errors.assignmentStart && (
            <p className="text-red-500 text-sm mt-1">{errors.assignmentStart.message}</p>
          )}
        </div>

        {/* Assignment End */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('shiftAssignment.form.field.assignmentEnd')} *
          </label>
          <Controller
            name="assignmentEnd"
            control={control}
            render={({ field }) => {
              // Convert string to Date if needed
              const dateValue = field.value instanceof Date 
                ? field.value 
                : (typeof field.value === 'string' ? parseDateString(field.value) : null) || MAX_DATE;
              
              return (
                <DateTimePickerComponent
                  value={dateValue}
                  onChange={(e: any) => {
                    // DateTimePicker returns Date object or null
                    field.onChange(e.value);
                  }}
                  placeholder={t('shiftAssignment.form.field.assignmentEnd')}
                  cssClass={errors.assignmentEnd ? 'e-error' : ''}
                  format={i18n.language === 'de' ? 'dd.MM.yyyy HH:mm' : 'MM/dd/yyyy hh:mm a'}
                  locale="de-DE"
                  max={MAX_DATE}
                />
              );
            }}
          />
          {errors.assignmentEnd && (
            <p className="text-red-500 text-sm mt-1">{errors.assignmentEnd.message}</p>
          )}
        </div>

        {/* Shift Template */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('shiftAssignment.form.field.shiftTemplate')} *
          </label>
          <Controller
            name="shiftTemplateId"
            control={control}
            render={({ field }) => (
              <DropDownListComponent
                dataSource={(shiftTemplates && shiftTemplates.length > 0) ? shiftTemplates : localShiftTemplates}
                fields={{ text: 'name', value: 'uuid' }}
                placeholder={t('shiftAssignment.form.field.shiftTemplatePlaceholder')}
                value={field.value}
                change={(e: any) => {
                  field.onChange(e.value);
                }}
                cssClass={errors.shiftTemplateId ? 'e-error' : ''}
                allowFiltering={false}
              />
            )}
          />
          {errors.shiftTemplateId && (
            <p className="text-red-500 text-sm mt-1">{errors.shiftTemplateId.message}</p>
          )}
        </div>

        {/* Capacity Value */}
        <div>
          <div className="flex items-center gap-2 mb-1">
            <label className="block text-sm font-medium text-gray-700">
              {t('shiftAssignment.form.field.capacityValue')} *
            </label>
            {isSingleResource && (
              <span
                className="text-blue-600 cursor-help text-sm"
                title={t('shiftAssignment.form.singleResourceCapacityLocked')}
                style={{ fontSize: '14px' }}
              >
                ⓘ
              </span>
            )}
          </div>
          <Controller
            name="capacityValue"
            control={control}
            render={({ field }) => (
              <NumericTextBoxComponent
                value={field.value}
                change={(e: any) => {
                  // Enforce max value for single resource machines
                  const newValue = isSingleResource && e.value > 1.0 ? 1.0 : e.value;
                  field.onChange(newValue);
                }}
                placeholder={t('shiftAssignment.form.field.capacityValue')}
                cssClass={errors.capacityValue ? 'e-error' : ''}
                min={0.1}
                max={isSingleResource ? 1.0 : undefined}
                step={0.1}
                format="N2"
                locale="de-DE"
                enabled={!isSingleResource}
              />
            )}
          />
          {errors.capacityValue && (
            <p className="text-red-500 text-sm mt-1">{errors.capacityValue.message}</p>
          )}
        </div>

        {/* Overwrites Lower Priority */}
        <div className="flex items-center gap-2">
          <Controller
            name="overwritesLowerPriority"
            control={control}
            render={({ field }) => (
              <CheckBoxComponent
                checked={Boolean(field.value)}
                change={(e: any) => {
                  field.onChange(!!e.checked);
                }}
                disabled={isSingleResource}
              />
            )}
          />
          <span className="text-sm font-medium text-gray-700">
            {t('shiftAssignment.form.field.overwritesLowerPriority')}
          </span>
          {isSingleResource && (
            <span
              className="text-blue-600 cursor-help text-sm"
              title={t('shiftAssignment.form.singleResourceOverwriteLocked')}
              style={{ fontSize: '14px' }}
            >
              ⓘ
            </span>
          )}
        </div>

      </div>
    </DialogComponent>
  );
};

export default ShiftAssignmentFormDialog;